/**
 * Press-and-hold without swallowing a scroll. A grid is a scrolling surface, so a hold only counts
 * while the finger stays put: drag past a few pixels and neither the hold nor the tap fires, which
 * is what the reader meant. Pure handlers, so the rules are unit tested rather than clicked through.
 */

const MOVE_TOLERANCE_PX = 10;
const DEFAULT_DELAY_MS = 500;

type Point = { clientX: number; clientY: number };

export type PressHandlers = {
  onPointerDown: (e: Point) => void;
  onPointerUp: (e: Point) => void;
  onPointerMove: (e: Point) => void;
  onPointerCancel: () => void;
};

export function pressHandlers(o: {
  onPress: () => void;
  onLongPress: () => void;
  delayMs?: number;
}): PressHandlers {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let start: Point | null = null;
  let fired = false;

  const stop = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    start = null;
  };

  return {
    onPointerDown(e) {
      fired = false;
      start = { clientX: e.clientX, clientY: e.clientY };
      timer = setTimeout(() => {
        fired = true;
        stop();
        o.onLongPress();
      }, o.delayMs ?? DEFAULT_DELAY_MS);
    },
    onPointerMove(e) {
      if (start === null) return;
      const far =
        Math.abs(e.clientX - start.clientX) > MOVE_TOLERANCE_PX ||
        Math.abs(e.clientY - start.clientY) > MOVE_TOLERANCE_PX;
      if (far) stop();
    },
    onPointerUp() {
      // A hold that already fired is not also a tap, and a cancelled or dragged press is neither.
      const wasPressing = start !== null;
      stop();
      if (!fired && wasPressing) o.onPress();
    },
    onPointerCancel() {
      fired = false;
      stop();
    },
  };
}
